import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

import { teamProvider } from '../../providers/teamProvider';
import { UserKindEnum } from '../../types/enums';
import type { TeamGetAllAnnouncersResponseType } from '../../types/responses';
import type { AnnouncerUserModel } from '../../types/team';
import type {
  PropertyAdEditorFormValues,
  PropertyAdInProgressWorkspace,
} from '../../types/propertyAdEditor';
import { getKindLabel } from '../../utils';

import { EditorSection } from './EditorSection';

interface ResponsibleUserSectionProps {
  workspace: Pick<PropertyAdInProgressWorkspace, 'propertyAdCreator' | 'userId'>;
}

interface ResponsibleOption {
  avatar: string | null;
  id: string;
  kind?: UserKindEnum;
  name: string;
}

const assignableKinds = new Set<UserKindEnum>([UserKindEnum.Broker, UserKindEnum.Manager]);

const getRoleLabel = (kind?: UserKindEnum) => (kind ? getKindLabel(kind) : 'Responsible');

const getHydratedResponsibleOption = (
  workspace: ResponsibleUserSectionProps['workspace']
): ResponsibleOption | null => {
  const creator = workspace.propertyAdCreator;
  if (
    !workspace.userId ||
    !creator ||
    creator.id !== workspace.userId ||
    !creator.name ||
    !creator.kind ||
    !assignableKinds.has(creator.kind)
  )
    return null;

  return {
    avatar: creator.avatar,
    id: creator.id,
    kind: creator.kind,
    name: creator.name,
  };
};

const toResponsibleOption = (user: AnnouncerUserModel): ResponsibleOption => ({
  avatar: user.avatar || null,
  id: user.id,
  kind: user.kind,
  name: user.name,
});

const mergeResponsibleOptions = (options: Array<ResponsibleOption | null>): ResponsibleOption[] => {
  const uniqueOptions = new Map<string, ResponsibleOption>();

  options.forEach((option) => {
    if (!option?.kind || !assignableKinds.has(option.kind)) return;
    uniqueOptions.set(option.id, option);
  });

  return Array.from(uniqueOptions.values());
};

export const ResponsibleUserSection = ({ workspace }: ResponsibleUserSectionProps) => {
  const { control } = useFormContext<PropertyAdEditorFormValues>();
  const [announcers, setAnnouncers] = useState<ResponsibleOption[]>([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [requestAttempt, setRequestAttempt] = useState(0);
  const hydratedResponsible = useMemo(() => getHydratedResponsibleOption(workspace), [workspace]);

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    setError('');

    const loadAnnouncers = async () => {
      try {
        const response = await teamProvider.getAnnouncers();
        if (!isActive) return;

        if (!response.ok) {
          setAnnouncers([]);
          setError(response.message || "Couldn't load responsible users. Please try again.");
          return;
        }

        const data = response.data as TeamGetAllAnnouncersResponseType | null;
        setAnnouncers(
          (data?.items ?? [])
            .map(({ user }) => toResponsibleOption(user))
            .filter(({ kind }) => kind && assignableKinds.has(kind))
        );
      } catch (requestError) {
        if (!isActive) return;
        setAnnouncers([]);
        setError(
          requestError instanceof Error
            ? requestError.message
            : "Couldn't load responsible users. Please try again."
        );
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    void loadAnnouncers();

    return () => {
      isActive = false;
    };
  }, [requestAttempt]);

  const options = useMemo(() => mergeResponsibleOptions(announcers), [announcers]);

  return (
    <EditorSection
      cardTestId='responsible-user-card'
      description='Choose who will be responsible for this listing'
      title='Responsible for announcement'
      titleId='responsible-user-title'
    >
      <Stack minWidth={0} spacing={2.5}>
        <Controller<PropertyAdEditorFormValues, 'responsibleUserId'>
          control={control}
          name='responsibleUserId'
          render={({ field }) => {
            const selectedResponsible =
              options.find(({ id }) => id === field.value) ??
              (hydratedResponsible?.id === field.value ? hydratedResponsible : null);

            return (
              <>
                <Autocomplete<ResponsibleOption, false, false, false>
                  autoHighlight
                  disabled={isLoading || options.length === 0}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  loading={isLoading}
                  noOptionsText='No responsible users available'
                  onBlur={field.onBlur}
                  onChange={(_, option) => field.onChange(option?.id ?? null)}
                  options={options}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputRef={field.ref}
                      label='Responsible for announcement'
                      name={field.name}
                      placeholder='Choose responsible for announcement'
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoading && <CircularProgress color='inherit' size={20} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component='li' {...props}>
                      <Avatar alt={option.name} src={option.avatar ?? undefined} />
                      <Stack marginLeft={1.5} minWidth={0}>
                        <Typography noWrap>{option.name}</Typography>
                        <Typography color='text.secondary' variant='body2'>
                          {getRoleLabel(option.kind)}
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                  value={selectedResponsible}
                />

                {selectedResponsible && (
                  <Stack
                    alignItems='center'
                    aria-label={`Current responsible: ${selectedResponsible.name}`}
                    component='article'
                    direction='row'
                    minWidth={0}
                    spacing={2}
                    sx={{
                      backgroundColor: '#f9fcff',
                      border: '1px solid #dce9f7',
                      borderRadius: 2,
                      padding: 2,
                    }}
                  >
                    <Avatar
                      alt={selectedResponsible.name}
                      src={selectedResponsible.avatar ?? undefined}
                    />
                    <Stack minWidth={0}>
                      <Typography fontWeight={600} noWrap>
                        {selectedResponsible.name}
                      </Typography>
                      <Typography color='text.secondary' variant='body2'>
                        {getRoleLabel(selectedResponsible.kind)}
                      </Typography>
                    </Stack>
                  </Stack>
                )}
              </>
            );
          }}
        />

        {error && (
          <Stack
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
          >
            <Typography color='error' component='p' role='alert' variant='body2'>
              {error}
            </Typography>
            <Button
              onClick={() => setRequestAttempt((attempt) => attempt + 1)}
              size='small'
              sx={{ minWidth: 0, padding: 0, textTransform: 'none' }}
              type='button'
              variant='text'
            >
              Retry
            </Button>
          </Stack>
        )}

        {!isLoading && !error && options.length === 0 && (
          <Typography color='text.secondary' component='p' variant='body2'>
            No responsible users available.
          </Typography>
        )}
      </Stack>
    </EditorSection>
  );
};
